import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
    Modal,
    ModalBodyFooter,
    ModalProcessing,
    Button,
    ErrorMessage
} from '../../../components/custom-essentials';
import { SelectSingle, FormikControl, DatePicker, Check } from '../../../components/form';
import { formatDateApi, convertApiToDate } from '../../../components/functions';
import { typeOptions, paymentFreqOptions, getInitStudentOption, getInitTypeOption, getInitPaymentFreqOption } from './helpers';

function MathPlexContractModal(props) {
    const {
        show = true,
        mode = 'create',
        contract,
        students = [],
        onClose,
        createContract,
        updateContract,
        deleteContract
    } = props;

    const [deleteProcessing, setDeleteProcessing] = useState(false);

    // Build student options - SelectSingle expects { value, label } objects
    const studentOptions = React.useMemo(() => {
        const opts = [{ value: -1, label: 'Please select...' }];
        if (Array.isArray(students)) {
            students.forEach(s => {
                if (s && s.user_id && s.first_name && s.last_name) {
                    opts.push({
                        value: s.user_id,
                        label: `${s.first_name} ${s.last_name}`.trim()
                    });
                }
            });
        }
        return opts;
    }, [students]);

    // Get initial values
    const getInitialValues = () => {
        const initStudent = getInitStudentOption(contract?.student, studentOptions);
        const initType = getInitTypeOption(contract?.type);
        const initPaymentFreq = getInitPaymentFreqOption(contract?.payment_frequency);

        return {
            student: initStudent || { value: -1, label: 'Please select...' },
            type: initType || { value: -1, label: 'Please select...' },
            startDate: contract?.start_date ? formatDateApi(contract.start_date) : formatDateApi(new Date()),
            endDate: contract?.end_date ? formatDateApi(contract.end_date) : '',
            paymentFrequency: initPaymentFreq || { value: -1, label: 'Please select...' },
            initialPayment: contract?.initial_payment != null ? contract.initial_payment : 0,
            amountPerPayment: contract?.amount_per_payment != null ? contract.amount_per_payment : 0,
            paymentDateOffset: contract?.payment_date_offset != null ? contract.payment_date_offset : 0,
            notes: contract?.notes != null ? contract.notes : '',
            setAsCurrent: contract ? (parseInt(contract.set_as_current) === 1) : false
        };
    };

    // Date range validation
    const validateDateRange = (startDate, endDate) => {
        if (!startDate || !endDate) return true; // Allow empty end date
        const start = convertApiToDate(startDate);
        const end = convertApiToDate(endDate);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
        return start < end;
    };

    const handleSubmit = async (values, formikHelpers) => {
        const { setSubmitting, setStatus } = formikHelpers;
        setStatus(null);

        // Extract values from option objects
        const studentId = values.student?.value;
        const contractType = values.type?.value;
        const paymentFreq = values.paymentFrequency?.value;

        // Validate selections
        if (!studentId || studentId === -1 || studentId === '-1') {
            setStatus('Please select a student.');
            setSubmitting(false);
            return;
        }

        if (!contractType || contractType === -1 || contractType === '-1') {
            setStatus('Please select a contract type.');
            setSubmitting(false);
            return;
        }

        if (!paymentFreq || paymentFreq === -1 || paymentFreq === '-1') {
            setStatus('Please select a payment frequency.');
            setSubmitting(false);
            return;
        }

        // Validate date range
        if (values.endDate && values.endDate.trim() && !validateDateRange(values.startDate, values.endDate)) {
            setStatus('Start date must come before end date.');
            setSubmitting(false);
            return;
        }

        // Build payload - backend expects specific format in req.body.params
        // Handle null endDate properly (SQL NULL, not string 'null' or empty string)
        let endDateValue = '';
        if (values.endDate && values.endDate.trim() && values.endDate.trim() !== '') {
            endDateValue = values.endDate.trim();
        }
        
        // Determine expires: if endDate is set, contract expires (1), otherwise doesn't expire (0)
        const expires = endDateValue ? 1 : 0;
        
        const payload = {
            student: String(studentId),
            type: String(contractType),
            startDate: values.startDate,
            endDate: endDateValue,
            expires: expires,
            paymentFrequency: String(paymentFreq),
            initialPayment: parseInt(values.initialPayment) || 0,
            amountPerPayment: parseInt(values.amountPerPayment) || 0,
            paymentDateOffset: parseInt(values.paymentDateOffset) || 0,
            notes: String(values.notes || '').trim().substring(0, 1000),
            setAsCurrent: values.setAsCurrent ? 1 : 0,
            showWarnings: 1, // Default to showing warnings
            warnMembers: 0 // Default to not warning members
        };

        try {
            const action = mode === 'edit' ? updateContract : createContract;
            const actionPayload = mode === 'edit' ? { ...payload, id: contract?.id } : payload;
            
            const response = await action(actionPayload);

            if (!response || response.status >= 400) {
                const errorMsg = response?.data?.message || 'Unable to save contract. Please try again.';
                setStatus(errorMsg);
                setSubmitting(false);
                return;
            }

            setSubmitting(false);
            if (onClose) {
                onClose(true);
            }
        } catch (error) {
            setStatus('An unexpected error occurred. Please try again.');
            setSubmitting(false);
        }
    };

    // Create validation schema without drsValid (we validate dates in handleSubmit)
    const validationSchema = Yup.object().shape({
        student: Yup.mixed().test(
            'studentIsSelected',
            'Please select a valid student',
            (value) => value && value.value !== -1
        ),
        type: Yup.mixed().test(
            'typeIsSelected',
            'Please select a valid type',
            (value) => value && value.value !== -1
        ),
        paymentFrequency: Yup.mixed().test(
            'paymentFreqIsSelected',
            'Please select a valid option',
            (value) => value && value.value !== -1
        ),
        initialPayment: Yup.number()
            .typeError('Initial Payment must be a number')
            .min(0, 'Min 0')
            .max(30000, 'Max 30000')
            .integer('Whole Number Required'),
        amountPerPayment: Yup.number()
            .typeError('Amount must be a number')
            .min(0, 'Min 0')
            .max(30000, 'Max 30000')
            .integer('Whole Number Required'),
        paymentDateOffset: Yup.number()
            .typeError('Offset must be a number')
            .min(0, 'Min 0')
            .max(30000, 'Max 30000')
            .integer('Whole Number Required'),
        notes: Yup.string()
            .max(1000, 'Max 1000 characters'),
        startDate: Yup.string().required('Start date is required')
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema,
        onSubmit: handleSubmit
    });

    const handleDelete = async () => {
        if (!contract?.id) return;
        
        setDeleteProcessing(true);
        try {
            const response = await deleteContract({ id: contract.id });
            if (!response || response.status >= 400) {
                setDeleteProcessing(false);
                return;
            }
            setDeleteProcessing(false);
            if (onClose) {
                onClose(true);
            }
        } catch (error) {
            setDeleteProcessing(false);
        }
    };

    const closeModal = (shouldRefresh = false) => {
        if (onClose) onClose(shouldRefresh);
    };

    if (!show) return null;

    const modalTitle = mode === 'edit' ? 'Edit Contract' : mode === 'delete' ? 'Delete Contract' : 'New Contract';

    // Delete confirmation
    if (mode === 'delete') {
        return (
            <Modal show={show} onHide={() => closeModal(false)} preventClickout={false} className="w-1/2">
                <Modal.Header><h2>{modalTitle}</h2></Modal.Header>
                <ModalBodyFooter>
                    <Modal.Body>
                        <h4 className="text-mpLRed mb-2">Delete this contract?</h4>
                        <p>This action cannot be undone.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="flex flex-row gap-x-2 flex-wrap items-center">
                            {deleteProcessing && <ModalProcessing />}
                            <Button color="mpLRed" onClick={() => closeModal(false)} disabled={deleteProcessing}>Cancel</Button>
                            <Button color="hol-mpLRed" onClick={handleDelete} disabled={deleteProcessing}>Delete</Button>
                        </div>
                    </Modal.Footer>
                </ModalBodyFooter>
            </Modal>
        );
    }

    // Form
    // Ensure formik is initialized before rendering
    if (!formik || !formik.values) {
        return null;
    }

    return (
        <Modal show={show} onHide={() => closeModal(false)} preventClickout={false} className="w-1/2">
            <Modal.Header><h2>{modalTitle}</h2></Modal.Header>
            <ModalBodyFooter>
                <Modal.Body>
                    <div className="grid grid-cols-1 gap-y-4">
                        <div>
                            <h4>Student</h4>
                            <SelectSingle
                                id="contract-student"
                                name="student"
                                value={formik.values.student}
                                options={studentOptions}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.student && formik.touched.student && (
                                <ErrorMessage color="mpLRed">{formik.errors.student}</ErrorMessage>
                            )}
                        </div>

                        <div>
                            <h4>Contract Type</h4>
                            <SelectSingle
                                id="contract-type"
                                name="type"
                                value={formik.values.type}
                                options={[{ value: -1, label: 'Please select...' }, ...typeOptions]}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.type && formik.touched.type && (
                                <ErrorMessage color="mpLRed">{formik.errors.type}</ErrorMessage>
                            )}
                        </div>

                        <div className="flex flex-row gap-x-4">
                            <div className="w-1/2">
                                <h4>Contract Start</h4>
                                <DatePicker
                                    id="contract-startDate"
                                    name="startDate"
                                    value={formik.values.startDate}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.startDate && formik.touched.startDate && (
                                    <ErrorMessage color="mpLRed">{formik.errors.startDate}</ErrorMessage>
                                )}
                            </div>
                            <div className="w-1/2">
                                <h4>Contract End</h4>
                                <DatePicker
                                    id="contract-endDate"
                                    name="endDate"
                                    value={formik.values.endDate}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.endDate && formik.touched.endDate && (
                                    <ErrorMessage color="mpLRed">{formik.errors.endDate}</ErrorMessage>
                                )}
                            </div>
                        </div>

                        <div>
                            <h4>Payment Frequency</h4>
                            <SelectSingle
                                id="contract-paymentFrequency"
                                name="paymentFrequency"
                                value={formik.values.paymentFrequency}
                                options={[{ value: -1, label: 'Please select...' }, ...paymentFreqOptions]}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.paymentFrequency && formik.touched.paymentFrequency && (
                                <ErrorMessage color="mpLRed">{formik.errors.paymentFrequency}</ErrorMessage>
                            )}
                        </div>

                        <div className="flex flex-row gap-x-4">
                            <div className="w-1/3">
                                <h4>Initial Payment</h4>
                                <FormikControl
                                    id="contract-initialPayment"
                                    name="initialPayment"
                                    type="number"
                                    value={formik.values.initialPayment}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.initialPayment && formik.touched.initialPayment && (
                                    <ErrorMessage color="mpLRed">{formik.errors.initialPayment}</ErrorMessage>
                                )}
                            </div>
                            <div className="w-1/3">
                                <h4>Amount Per Payment</h4>
                                <FormikControl
                                    id="contract-amountPerPayment"
                                    name="amountPerPayment"
                                    type="number"
                                    value={formik.values.amountPerPayment}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.amountPerPayment && formik.touched.amountPerPayment && (
                                    <ErrorMessage color="mpLRed">{formik.errors.amountPerPayment}</ErrorMessage>
                                )}
                            </div>
                            <div className="w-1/3">
                                <h4>Payment Date Offset</h4>
                                <FormikControl
                                    id="contract-paymentDateOffset"
                                    name="paymentDateOffset"
                                    type="number"
                                    value={formik.values.paymentDateOffset}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.paymentDateOffset && formik.touched.paymentDateOffset && (
                                    <ErrorMessage color="mpLRed">{formik.errors.paymentDateOffset}</ErrorMessage>
                                )}
                            </div>
                        </div>

                        <div>
                            <h4>Notes</h4>
                            <FormikControl
                                id="contract-notes"
                                name="notes"
                                as="textarea"
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                                placeholder="Max. 1000 characters"
                            />
                            {formik.errors.notes && formik.touched.notes && (
                                <ErrorMessage color="mpLRed">{formik.errors.notes}</ErrorMessage>
                            )}
                        </div>

                        <div>
                            <Check
                                id="contract-setAsCurrent"
                                name="setAsCurrent"
                                label="Set as Current Contract"
                                color="mpLRed"
                                checked={formik.values.setAsCurrent}
                                onChange={formik.handleChange}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-row gap-x-2 flex-wrap items-center">
                        {formik.isSubmitting && <ModalProcessing />}
                        {formik.status && <ErrorMessage color="mpLRed">{formik.status}</ErrorMessage>}
                        <Button color="mpLRed" onClick={() => closeModal(false)} disabled={formik.isSubmitting}>Cancel</Button>
                        <Button color="mpBlue" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
                            {mode === 'edit' ? 'Save Changes' : 'Add Contract'}
                        </Button>
                    </div>
                </Modal.Footer>
            </ModalBodyFooter>
        </Modal>
    );
}

export default MathPlexContractModal;

